import {
  MediaPlayerFullscreenWindowPosition,
  MediaPlayerViewMode,
} from "./types";
import {
  CAN_PLAY_THROUGH,
  CAN_PLAY,
  CAPTION_CHANGE,
  END,
  LOAD_START,
  LOADED_DATA,
  LOADED_META_DATA,
  PAUSE,
  PLAY,
  PLAYING,
  PROGRESS_CHANGE,
  SEEKED,
  TIME_UPDATE,
  USER_ACTIVITY,
  VIEWMODE_CHANGE,
  VOLUME_CHANGE,
  WAITING,
  RATE_CHANGE,
  CAPTION_TEXT_CHANGE,
  AIRPLAYING_CHANGE,
  CAN_AIRPLAY,
  PLAYLIST_POSITION_CHANGE,
  DURATION_CHANGE,
  SEEKING,
  SEEKING_TIME,
  VIEWMODE_FULLSCREEN_SLIDES_CHANGE,
  VIEWMODE_FULLSCREEN_WINDOW_POSITION_CHANGE,
} from "./constants";

import type { MediaPlayerAction, MediaChapter } from "./types";

export interface MediaElementState {
  error: MediaError | null;
  networkState: number;
  buffered: TimeRanges | null;
  readyState: number;
  currentTime: number;
  duration: number;
  paused: boolean;
  playbackRate: number;
  ended: boolean;
  volume: number;
  muted: boolean;
}

export interface MediaPlayerState extends MediaElementState {
  captionsEnabled: boolean;
  captionText: string;
  chapters: MediaChapter[] | null;
  fullscreen: boolean;
  hasStarted: boolean;
  hasUnmutedAutoplay: boolean;
  isLoading: boolean;
  seeking: boolean; // Our seeking value is not supplied by the video/media element
  seekingTime: number;
  userActivity: boolean;
  viewMode: MediaPlayerViewMode;
  viewModeFullscreenSlides: boolean;
  viewModeFullscreenWindowPosition: MediaPlayerFullscreenWindowPosition;
  waiting: boolean;
  airplaying: boolean;
  canAirplay: boolean;
  playlistPosition: number;
}

export const initialState: MediaPlayerState = {
  buffered: [] as unknown as TimeRanges,
  captionsEnabled: false,
  captionText: "",
  chapters: null,
  currentTime: 0,
  duration: 0,
  ended: false,
  error: null,
  fullscreen: false,
  hasStarted: false,
  hasUnmutedAutoplay: false,
  isLoading: false,
  muted: false,
  networkState: 0,
  paused: true,
  playbackRate: 1,
  playlistPosition: 0,
  readyState: 0,
  seeking: false,
  seekingTime: 0,
  userActivity: false,
  volume: 1,
  viewMode: MediaPlayerViewMode.inline,
  viewModeFullscreenSlides: false,
  viewModeFullscreenWindowPosition:
    MediaPlayerFullscreenWindowPosition.bottomLeft,
  waiting: true,
  airplaying: false,
  canAirplay: false,
};

export function reducer(
  state: MediaPlayerState,
  action: Partial<MediaPlayerAction>,
): MediaPlayerState {
  switch (action.type) {
    case USER_ACTIVITY:
      return {
        ...state,
        userActivity: Boolean(action.payload),
      };
    case LOAD_START:
      return {
        ...state,
        waiting: true,
        hasStarted: false,
        isLoading: true,
        ended: false,
      };
    case WAITING:
      return {
        ...state,
        ...action.mediaElementProperties,
        waiting: true,
      };
    case CAN_PLAY:
    case CAN_PLAY_THROUGH:
      return {
        ...state,
        ...action.mediaElementProperties,
        waiting: false,
      };
    case PLAY:
    case PLAYING:
      return {
        ...state,
        ...action.mediaElementProperties,
        ended: false,
        paused: false,
        waiting: false,
        hasStarted: true,
        isLoading: false,
      };
    case PAUSE:
      return {
        ...state,
        ...action.mediaElementProperties,
        paused: true,
      };
    case END:
      return {
        ...state,
        ...action.mediaElementProperties,
        ended: true,
        isLoading: false,
      };
    case SEEKED:
      return {
        ...state,
        ...action.mediaElementProperties,
        seeking: false,
      };
    case SEEKING:
      return {
        ...state,
        seeking: Boolean(action.payload),
      };
    case SEEKING_TIME:
      return {
        ...state,
        seekingTime: Number(action.payload),
      };
    case RATE_CHANGE:
      return {
        ...state,
        playbackRate: Number(action.payload),
      };
    case CAPTION_CHANGE:
      return {
        ...state,
        ...action.mediaElementProperties,
        captionsEnabled: Boolean(action.payload),
      };
    case CAPTION_TEXT_CHANGE:
      return {
        ...state,
        ...action.mediaElementProperties,
        captionText: String(action.payload),
      };
    case VIEWMODE_CHANGE:
      return {
        ...state,
        ...action.mediaElementProperties,
        viewMode: action.payload as MediaPlayerViewMode,
        fullscreen: action.payload == MediaPlayerViewMode.fullscreen,
      };
    case VIEWMODE_FULLSCREEN_SLIDES_CHANGE:
      return {
        ...state,
        ...action.mediaElementProperties,
        viewModeFullscreenSlides: action.payload as boolean,
      };
    case VIEWMODE_FULLSCREEN_WINDOW_POSITION_CHANGE:
      return {
        ...state,
        ...action.mediaElementProperties,
        viewModeFullscreenWindowPosition:
          action.payload as MediaPlayerFullscreenWindowPosition,
      };
    case LOADED_META_DATA:
    case LOADED_DATA:
      return {
        ...state,
        ...action.mediaElementProperties,
        readyState: action.payload ?? state.readyState,
      };
    case DURATION_CHANGE:
    case TIME_UPDATE:
    case PROGRESS_CHANGE:
    case VOLUME_CHANGE:
      return {
        ...state,
        ...action.mediaElementProperties,
      };
    case AIRPLAYING_CHANGE:
      return {
        ...state,
        ...action.mediaElementProperties,
        airplaying: Boolean(action.payload),
      };
    case CAN_AIRPLAY:
      return {
        ...state,
        ...action.mediaElementProperties,
        canAirplay: Boolean(action.payload),
      };
    case PLAYLIST_POSITION_CHANGE:
      return {
        ...state,
        ...action.mediaElementProperties,
        playlistPosition: Number(action.payload),
        currentTime: 0,
        paused: false,
        waiting: true,
      };
    default:
      return state;
  }
}
